/**
 * The title's limits on absence as meters on the band (owner, 22 Oct 2026):
 * for a temporary permit "In total · 8 months in the permit" and "In a row ·
 * 6 months per trip", each a bar of what one trip has taken of it and the
 * count beside it ("65 / 244"). The one the answer's number comes from carries
 * the answer's hourglass — the mark that ties the number to its limit, never a
 * word like "first". Every figure and word is the shared presentation's
 * (`inoutLimitCounters`, `inoutLimitMeterText`); a rule with no number shows
 * none (AC-2). Its rule in full, trip by trip, is the Limits tab's.
 */
import { Hourglass } from '@tonnie/assets';
import {
  INOUT_CLOCK_STATUS_KEY,
  inoutLimitCounters,
  inoutLimitMeterText,
  type InOutEvaluation,
} from '@tonnie/heroes';
import { useLocale } from '@tonnie/locale';
import { cn } from '@tonnie/ui';

export function InOutLimitMeters({ evaluation }: { evaluation: InOutEvaluation }) {
  const { t, formatNumber } = useLocale();
  const counters = inoutLimitCounters(evaluation);
  if (counters.length === 0) return null;

  return (
    <ul
      className="mt-4 flex w-full max-w-2xl flex-col gap-3 self-stretch @md/hero-content:gap-2.5"
      aria-label={t('inout.meter.title')}
      data-testid="inout-banner-limits"
    >
      {counters.map(({ clock, figures, definesAnswer }) => {
        const { name, detail } = inoutLimitMeterText(clock, t, formatNumber);
        return (
          <li
            key={clock.constraintId}
            // Narrow: the name and the count on one line, the bar under them. Wide: one row —
            // name, bar, count — so the band keeps the heroes' height (owner, 22 Oct 2026).
            className="grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-end gap-x-3 gap-y-1.5 text-left text-white @md/hero-content:grid-cols-[1.25rem_minmax(0,14rem)_minmax(0,1fr)_auto] @md/hero-content:items-center"
            data-testid="inout-banner-limit"
            data-constraint={clock.constraintId}
            data-defines-answer={definesAnswer}
          >
            <span className="flex h-5 w-5 items-center justify-center self-center">
              {definesAnswer && (
                <Hourglass
                  className="h-4 w-4"
                  aria-label={t('inout.meter.definesAnswer')}
                  data-testid="inout-banner-limit-mark"
                />
              )}
            </span>
            <span className="col-start-2 row-start-1 min-w-0 leading-tight">
              <span className="text-sm font-bold">{name}</span>
              {detail && <span className="ml-2 text-2xs text-white/80">{detail}</span>}
            </span>
            {figures ? (
              <span
                className="col-start-3 row-start-1 text-sm font-bold tabular-nums @md/hero-content:col-start-4"
                data-testid="inout-banner-limit-counted"
              >
                {t('inout.meter.fraction', {
                  taken: formatNumber(figures.taken),
                  limit: formatNumber(figures.limit),
                })}
                {clock.status !== 'WITHIN' && (
                  <span
                    className="ml-2 rounded-pill bg-white/20 px-2 py-0.5 text-2xs font-bold"
                    data-testid="inout-banner-limit-status"
                  >
                    {t(INOUT_CLOCK_STATUS_KEY[clock.status])}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-xs text-white/80">{t('inout.clock.noNumber')}</span>
            )}
            {figures && (
              <span
                className="col-start-2 col-end-4 row-start-2 h-2 overflow-hidden rounded-pill bg-white/20 @md/hero-content:col-start-3 @md/hero-content:row-start-1"
                role="meter"
                aria-valuemin={0}
                aria-valuemax={figures.limit}
                aria-valuenow={figures.taken}
                aria-valuetext={t('inout.meter.valueText', {
                  taken: formatNumber(figures.taken),
                  limit: formatNumber(figures.limit),
                  left: formatNumber(figures.left),
                })}
                aria-label={name}
              >
                <span
                  className={cn(
                    'block h-full rounded-pill',
                    clock.status === 'WITHIN' ? 'bg-white' : 'bg-warning'
                  )}
                  style={{ width: `${Math.max(figures.share * 100, figures.taken > 0 ? 2 : 0)}%` }}
                />
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
